import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from '../../hooks/use-toast';
import {
  Package, FileText, Camera, Upload, CheckCircle2, XCircle, AlertTriangle,
  Zap, Building2, Clock, RotateCcw, Loader2, ArrowRight, ScanLine, History,
  UploadCloud, Printer, FileSpreadsheet,
} from 'lucide-react';
import { exportToCSV, printTable } from '../../lib/printUtils';

const formatCLP = (v) => new Intl.NumberFormat('es-CL').format(Math.round(v || 0));

/**
 * Pantalla dedicada al bodeguero para recepcionar OCs con factura escaneada.
 * Flujo por pasos:
 *   1. Ingreso de OC + N° factura
 *   2. Validación proveedor (modal de confirmación)
 *   3. Recepción item por item (con botón "recibir todo")
 *   4. Foto/archivo de factura
 *   5. Confirmación → crea recepción + sube archivo + actualiza stock
 */
export default function RecepcionOCPage() {
  const [step, setStep] = useState(1); // 1..4
  // Paso 1
  const [numeroOC, setNumeroOC] = useState('');
  const [folioFactura, setFolioFactura] = useState('');
  const [rutFactura, setRutFactura] = useState('');
  const [tipoDocumento, setTipoDocumento] = useState('FA');
  const [loading, setLoading] = useState(false);
  // Paso 2+3
  const [ocData, setOcData] = useState(null);
  const [validationData, setValidationData] = useState(null);
  const [cantidades, setCantidades] = useState({}); // item → valor
  // Paso 4
  const [facturaFile, setFacturaFile] = useState(null);
  const [facturaPreview, setFacturaPreview] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  // Scanner: carpeta del escáner (File System Access API)
  const [scannerFiles, setScannerFiles] = useState([]); // [{name, size, modified, file}]
  const [scannerLoading, setScannerLoading] = useState(false);
  const [scannerShown, setScannerShown] = useState(false);
  const [nuevoEscaneo, setNuevoEscaneo] = useState(null); // archivo recién detectado
  const dirHandleRef = useRef(null);
  const lastSeenTsRef = useRef(0); // mtime del último archivo ya visto
  const pollRef = useRef(null);
  const fsaSupported = typeof window !== 'undefined' && 'showDirectoryPicker' in window;
  // Historial
  const [historial, setHistorial] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedRecepcion, setSavedRecepcion] = useState(null);

  const loadHistorial = useCallback(async () => {
    try {
      const res = await api.get('/bodega/recepcion-oc/historial-dia');
      setHistorial(res.data.items || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadHistorial(); }, [loadHistorial]);

  // Carlos (Feb 2026): imprimir y exportar el historial de recepciones del día
  const imprimirHistorial = () => {
    if (!historial.length) {
      toast({ title: 'No hay recepciones para imprimir', variant: 'destructive' });
      return;
    }
    printTable({
      title: 'Recepciones de OC del Día',
      subtitle: `${historial.length} recepciones · ${new Date().toLocaleString('es-CL')}`,
      summary: [
        { label: 'Total recepciones', value: historial.length },
        { label: 'Con archivo factura', value: historial.filter((h) => h.tiene_archivo).length },
      ],
      columns: [
        { key: 'numero_recepcion', label: 'N° Recepción', mono: true, align: 'center' },
        { key: 'numero_oc', label: 'N° OC', align: 'center' },
        { key: 'numero_documento', label: 'N° Factura', mono: true },
        { key: 'tipo_documento', label: 'Tipo Doc.', align: 'center' },
        { key: 'razon_social_proveedor', label: 'Proveedor', format: (v, r) => v || r.proveedor || r.rut_proveedor || '' },
        { key: 'fecha', label: 'Fecha', format: (v) => v ? new Date(v).toLocaleString('es-CL') : '' },
        { key: 'tiene_archivo', label: 'Archivo', align: 'center', format: (v) => v ? 'Sí' : 'No' },
      ],
      rows: historial,
    });
  };

  const exportarHistorialExcel = () => {
    if (!historial.length) {
      toast({ title: 'No hay recepciones para exportar', variant: 'destructive' });
      return;
    }
    exportToCSV({
      filename: 'recepciones_oc_dia',
      title: 'CHIRINO STEEL · Recepciones de OC del Día',
      summary: [
        { label: 'Total recepciones', value: historial.length },
        { label: 'Con archivo factura', value: historial.filter((h) => h.tiene_archivo).length },
        { label: 'Generado', value: new Date().toLocaleString('es-CL') },
      ],
      columns: [
        { key: 'numero_recepcion', label: 'N° Recepción' },
        { key: 'numero_oc', label: 'N° OC' },
        { key: 'numero_documento', label: 'N° Factura' },
        { key: 'tipo_documento', label: 'Tipo Documento' },
        { key: 'rut_proveedor', label: 'RUT Proveedor' },
        { key: 'razon_social_proveedor', label: 'Proveedor', format: (v, r) => v || r.proveedor || '' },
        { key: 'fecha', label: 'Fecha', format: (v) => v ? new Date(v).toLocaleString('es-CL') : '' },
        { key: 'recibido_por', label: 'Recibido por' },
        { key: 'tiene_archivo', label: 'Tiene Archivo', format: (v) => v ? 'Sí' : 'No' },
        { key: 'observaciones', label: 'Observaciones' },
      ],
      rows: historial,
    });
  };

  const validar = async () => {
    if (!numeroOC) return toast({ title: 'Ingrese N° OC', variant: 'destructive' });
    if (!folioFactura) return toast({ title: 'Ingrese N° Factura', variant: 'destructive' });
    setLoading(true);
    try {
      const params = new URLSearchParams({ numero_oc: numeroOC, folio_factura: folioFactura });
      if (rutFactura) params.set('rut_proveedor_factura', rutFactura);
      const res = await api.get(`/bodega/recepcion-oc/validar?${params}`);
      setValidationData(res.data);
      setOcData(res.data);
      if (res.data.factura_ya_recepcionada) {
        toast({ title: '⚠️ Factura ya recepcionada', description: 'Este folio ya tiene recepción registrada. Verifica antes de continuar.', variant: 'destructive' });
      }
      // Pre-llenar cantidades con el pendiente
      const init = {};
      (res.data.items || []).forEach((it) => {
        init[it.item] = it.cantidad_pendiente > 0 ? it.cantidad_pendiente : 0;
      });
      setCantidades(init);
      setStep(2);
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'OC no encontrada', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const confirmarProveedor = () => setStep(3);
  const volverInicio = () => {
    setStep(1); setOcData(null); setValidationData(null); setCantidades({});
    setFacturaFile(null); setFacturaPreview(null); setSavedRecepcion(null);
  };

  const recibirTodo = () => {
    const init = {};
    (ocData.items || []).forEach((it) => {
      init[it.item] = it.cantidad_pendiente;
    });
    setCantidades(init);
    toast({ title: 'Cantidades precargadas al 100% pendiente' });
  };

  const updateCantidad = (itemNum, valor) => {
    setCantidades((prev) => ({ ...prev, [itemNum]: parseFloat(valor) || 0 }));
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFacturaFile(f);
    if (f.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setFacturaPreview(ev.target.result);
      reader.readAsDataURL(f);
    } else {
      setFacturaPreview(null);
    }
  };

  // ═══ Scanner: conectar con carpeta de escáner (File System Access API) ═══
  const conectarEscaner = async () => {
    if (!fsaSupported) {
      toast({
        title: 'Tu navegador no soporta carpetas',
        description: 'Usa Chrome o Edge, o selecciona el archivo manualmente desde "Subir PDF/imagen".',
        variant: 'destructive',
      });
      return;
    }
    try {
      const handle = await window.showDirectoryPicker({
        id: 'escaner-chirino',
        mode: 'read',
        startIn: 'documents',
      });
      dirHandleRef.current = handle;
      // Al conectar por primera vez, marcar el archivo más reciente existente como "ya visto"
      // para que solo detectemos los NUEVOS a partir de ahora.
      const files = await leerArchivosCarpeta();
      lastSeenTsRef.current = files[0]?.modified || Date.now();
      setScannerShown(true);
      setScannerFiles(files);
      toast({ title: `📁 Carpeta conectada · monitoreando nuevos escaneos` });
    } catch (err) {
      if (err.name !== 'AbortError') {
        toast({ title: 'No se pudo abrir la carpeta', description: String(err), variant: 'destructive' });
      }
    }
  };

  const leerArchivosCarpeta = async () => {
    if (!dirHandleRef.current) return [];
    const files = [];
    for await (const entry of dirHandleRef.current.values()) {
      if (entry.kind !== 'file') continue;
      const name = entry.name.toLowerCase();
      if (!name.endsWith('.pdf') && !name.endsWith('.jpg') && !name.endsWith('.jpeg') && !name.endsWith('.png')) continue;
      try {
        const f = await entry.getFile();
        files.push({ name: entry.name, size: f.size, modified: f.lastModified, file: f });
      } catch { /* skip */ }
    }
    files.sort((a, b) => b.modified - a.modified);
    return files.slice(0, 10);
  };

  const listarArchivosEscaner = async () => {
    if (!dirHandleRef.current) return;
    setScannerLoading(true);
    try {
      const files = await leerArchivosCarpeta();
      setScannerFiles(files);
    } catch (err) {
      toast({ title: 'Error al listar archivos', description: String(err), variant: 'destructive' });
    } finally {
      setScannerLoading(false);
    }
  };

  const seleccionarDelEscaner = (sf) => {
    setFacturaFile(sf.file);
    if (sf.file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setFacturaPreview(ev.target.result);
      reader.readAsDataURL(sf.file);
    } else {
      setFacturaPreview(null);
    }
    lastSeenTsRef.current = sf.modified;
    setNuevoEscaneo(null);
    toast({ title: `Seleccionado: ${sf.name}` });
  };

  const usarUltimoEscaneo = async () => {
    if (!dirHandleRef.current) {
      await conectarEscaner();
      return;
    }
    setScannerLoading(true);
    try {
      const files = await leerArchivosCarpeta();
      setScannerFiles(files);
      if (files.length === 0) {
        toast({ title: 'Carpeta vacía', description: 'Escanea primero y vuelve a intentar.', variant: 'destructive' });
        return;
      }
      seleccionarDelEscaner(files[0]);
    } finally {
      setScannerLoading(false);
    }
  };

  // Auto-polling: detectar archivos nuevos cada 2s cuando estamos en paso 4
  useEffect(() => {
    if (step !== 4 || !dirHandleRef.current) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const files = await leerArchivosCarpeta();
        if (files.length > 0 && files[0].modified > lastSeenTsRef.current + 500) {
          setNuevoEscaneo(files[0]);
          setScannerFiles(files);
        }
      } catch { /* ignore */ }
    }, 2000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [step, scannerShown]);

  const totalRecep = () => {
    if (!ocData) return 0;
    return ocData.items.reduce((a, it) => a + (cantidades[it.item] || 0) * it.valor_unitario, 0);
  };

  const itemsParaEnviar = () => {
    if (!ocData) return [];
    return ocData.items
      .filter((it) => (cantidades[it.item] || 0) > 0)
      .map((it) => ({
        item: it.item,
        descripcion: it.descripcion,
        codigo_material: it.codigo_material || '',
        cantidad_pedida: it.cantidad_pedida,
        cantidad_recepcionada: cantidades[it.item] || 0,
        valor_unitario: it.valor_unitario,
        total: (cantidades[it.item] || 0) * it.valor_unitario,
        ot_numero: it.ot_numero ? parseInt(it.ot_numero) : null,
      }));
  };

  const guardarRecepcion = async () => {
    const items = itemsParaEnviar();
    if (items.length === 0) return toast({ title: 'Debe recepcionar al menos un ítem', variant: 'destructive' });
    if (!facturaFile) {
      const ok = window.confirm('No has adjuntado la factura. ¿Continuar igual?');
      if (!ok) return;
    }
    setSaving(true);
    try {
      const payload = {
        numero_oc: numeroOC,
        fecha: new Date().toISOString().split('T')[0],
        tipo_documento: tipoDocumento,
        numero_documento: folioFactura,
        responsable: 'Bodega',
        guia_despacho: folioFactura,
        destino: 'bodega',
        items,
      };
      const formData = new FormData();
      formData.append('payload', JSON.stringify(payload));
      if (facturaFile) formData.append('factura', facturaFile);
      const res = await api.post('/bodega/recepcion-oc/recepcionar-con-factura', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSavedRecepcion(res.data);
      toast({ title: `Recepción ${res.data.numero_recepcion} creada ✓` });
      loadHistorial();
      setStep(5);
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error al guardar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // === PASO 5: Confirmación exitosa ===
  if (step === 5 && savedRecepcion) {
    return (
      <div className="max-w-2xl mx-auto space-y-4 p-4" data-testid="recepcion-success">
        <Card className="border-emerald-500 border-2">
          <CardContent className="p-6 text-center space-y-3">
            <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-12 h-12 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-emerald-700">¡Recepción Creada!</h2>
            <div className="text-4xl font-mono font-bold tabular-nums">{savedRecepcion.numero_recepcion}</div>
            <div className="text-sm text-muted-foreground">
              OC <strong>{numeroOC}</strong> · Factura <strong>{folioFactura}</strong>
            </div>
            {savedRecepcion.factura_subida ? (
              <Badge className="bg-emerald-500/10 text-emerald-700"><UploadCloud className="w-3 h-3 mr-1" /> Factura subida a SharePoint</Badge>
            ) : facturaFile ? (
              <Badge variant="destructive"><AlertTriangle className="w-3 h-3 mr-1" /> No se pudo subir la factura — sube manualmente</Badge>
            ) : null}
            <div className="pt-4 flex gap-2 justify-center flex-wrap">
              <Button
                variant="outline"
                className="rounded-xl"
                onClick={async () => {
                  try {
                    const res = await api.get(
                      `/bodega/recepcion/${savedRecepcion.numero_recepcion}/pdf`,
                      { responseType: 'blob' },
                    );
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `Recepcion_${savedRecepcion.numero_recepcion}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (err) {
                    toast({ title: 'Error al descargar PDF', description: err?.message, variant: 'destructive' });
                  }
                }}
                data-testid="btn-descargar-pdf-recepcion"
              >
                <Printer className="w-4 h-4 mr-1.5" /> Descargar PDF
              </Button>
              <Button onClick={volverInicio} className="rounded-xl" data-testid="btn-otra-recepcion">
                <RotateCcw className="w-4 h-4 mr-1.5" /> Otra Recepción
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-4 p-2 md:p-4" data-testid="recepcion-oc-page">
      <div className="flex items-center gap-2">
        <Package className="w-6 h-6 text-blue-600" />
        <h1 className="text-xl font-semibold">Recepción de OC</h1>
        <Badge variant="outline" className="ml-auto font-mono">Paso {step} de 4</Badge>
      </div>

      {/* Historial del día */}
      {historial.length > 0 && step === 1 && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2 text-muted-foreground">
                <History className="w-4 h-4" /> Mis recepciones de hoy ({historial.length})
              </CardTitle>
              <div className="flex gap-1.5 print:hidden">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={imprimirHistorial}
                  className="h-7 text-xs"
                  title="Imprimir recepciones del día"
                  data-testid="btn-print-recepciones-dia"
                >
                  <Printer className="w-3 h-3 mr-1" /> Imprimir
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportarHistorialExcel}
                  className="h-7 text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                  title="Exportar a Excel"
                  data-testid="btn-excel-recepciones-dia"
                >
                  <FileSpreadsheet className="w-3 h-3 mr-1" /> Excel
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y text-xs max-h-32 overflow-y-auto">
              {historial.map((h) => (
                <div key={h.numero_recepcion} className="flex items-center gap-2 px-3 py-1.5" data-testid={`hist-row-${h.numero_recepcion}`}>
                  <CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                  <span className="font-mono font-semibold">{h.numero_recepcion}</span>
                  <span className="text-muted-foreground">OC {h.numero_oc}</span>
                  <span className="text-muted-foreground">Fact {h.numero_documento}</span>
                  {h.tiene_archivo && <UploadCloud className="w-3 h-3 text-emerald-600 ml-auto" title="Factura subida" />}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ═══════════════ PASO 1 ═══════════════ */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ScanLine className="w-5 h-5 text-blue-600" /> Ingresa OC y Factura
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-semibold">N° Orden de Compra *</Label>
                <Input
                  value={numeroOC}
                  onChange={(e) => setNumeroOC(e.target.value)}
                  placeholder="Ej. 18597"
                  className="h-14 text-xl font-mono font-bold tabular-nums text-center"
                  autoFocus
                  data-testid="input-numero-oc"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold">N° Factura *</Label>
                <Input
                  value={folioFactura}
                  onChange={(e) => setFolioFactura(e.target.value)}
                  placeholder="Ej. 12345"
                  className="h-14 text-xl font-mono font-bold tabular-nums text-center"
                  data-testid="input-folio-factura"
                />
              </div>
              <div>
                <Label className="text-xs">Tipo Documento</Label>
                <select
                  value={tipoDocumento}
                  onChange={(e) => setTipoDocumento(e.target.value)}
                  className="w-full h-9 px-3 rounded-md border text-sm"
                  data-testid="select-tipo-doc"
                >
                  <option value="FA">Factura Afecta (33)</option>
                  <option value="FE">Factura Exenta (34)</option>
                  <option value="GD">Guía de Despacho</option>
                  <option value="BO">Boleta</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">RUT Proveedor según factura (opcional, para validar)</Label>
                <Input
                  value={rutFactura}
                  onChange={(e) => setRutFactura(e.target.value)}
                  placeholder="76.123.456-7"
                  className="h-9 text-sm font-mono"
                  data-testid="input-rut-factura"
                />
              </div>
            </div>
            <Button
              onClick={validar}
              disabled={loading || !numeroOC || !folioFactura}
              className="w-full h-14 text-lg rounded-xl"
              data-testid="btn-validar-oc"
            >
              {loading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <ArrowRight className="w-5 h-5 mr-2" />}
              Buscar OC y Validar
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ═══════════════ PASO 2: Confirmar proveedor ═══════════════ */}
      {step === 2 && ocData && (
        <Card className={`border-2 ${validationData.match_proveedor === false ? 'border-red-500' : 'border-amber-400'}`}>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Building2 className="w-5 h-5" /> Verifica el Proveedor
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-4 text-center space-y-1">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">OC {numeroOC} dice:</div>
              <div className="text-2xl font-bold">{ocData.oc.nombre_proveedor}</div>
              <div className="text-sm font-mono text-muted-foreground">RUT {ocData.oc.rut_proveedor}</div>
            </div>
            <div className="text-center py-2">
              <div className="text-sm font-medium">Compárala con la factura <strong>Nº {folioFactura}</strong> que tienes en la mano.</div>
              {validationData.match_proveedor === false && (
                <div className="mt-2 text-red-700 text-sm font-semibold bg-red-50 dark:bg-red-950/40 rounded-lg p-2 border border-red-300">
                  ⚠️ El RUT ingresado ({rutFactura}) NO coincide con el de la OC ({ocData.oc.rut_proveedor})
                </div>
              )}
              {validationData.factura_ya_recepcionada && (
                <div className="mt-2 text-amber-700 text-sm font-semibold bg-amber-50 dark:bg-amber-950/40 rounded-lg p-2 border border-amber-300">
                  ⚠️ Factura {folioFactura} ya recepcionada en esta OC. Revisa antes de continuar.
                </div>
              )}
              {validationData.dte_match && (
                <div className="mt-2 text-emerald-700 text-sm bg-emerald-50 dark:bg-emerald-950/40 rounded-lg p-2 border border-emerald-300">
                  ✓ Factura encontrada en SII · Total <strong>${formatCLP(validationData.dte_match.total)}</strong>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                onClick={volverInicio}
                className="h-14 rounded-xl text-red-600 border-red-300 hover:bg-red-50"
                data-testid="btn-no-coincide"
              >
                <XCircle className="w-5 h-5 mr-1.5" /> No coincide
              </Button>
              <Button
                onClick={confirmarProveedor}
                className="h-14 rounded-xl bg-emerald-600 hover:bg-emerald-700"
                data-testid="btn-si-coincide"
              >
                <CheckCircle2 className="w-5 h-5 mr-1.5" /> Sí, coincide
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ═══════════════ PASO 3: Recepcionar items ═══════════════ */}
      {step === 3 && ocData && (
        <>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                <Package className="w-5 h-5" />
                Ítems a Recepcionar
                <Badge variant="outline" className="ml-auto">{ocData.items.length} ítems</Badge>
                <Button size="sm" variant="outline" onClick={recibirTodo} data-testid="btn-recibir-todo" className="rounded-xl">
                  <Zap className="w-3.5 h-3.5 mr-1" /> Recibir 100% pendiente
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {ocData.items.map((it) => {
                  const cant = cantidades[it.item] || 0;
                  const excede = cant > it.cantidad_pendiente && it.cantidad_pendiente > 0;
                  const parcial = cant > 0 && cant < it.cantidad_pendiente;
                  return (
                    <div key={it.item} className="p-3 space-y-2" data-testid={`item-row-${it.item}`}>
                      <div className="flex items-start gap-2">
                        <span className="text-[10px] text-muted-foreground font-mono w-6 text-right flex-shrink-0 pt-1">{it.item}</span>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm">{it.descripcion}</div>
                          <div className="text-xs text-muted-foreground">
                            {it.codigo_material && <span className="font-mono mr-2">{it.codigo_material}</span>}
                            <span>Unidad: <strong>{it.unidad || 'UND'}</strong></span>
                            {it.ot_numero && <span className="ml-2">OT: <strong>{it.ot_numero}</strong></span>}
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-1 text-center text-xs">
                        <div className="bg-slate-100 dark:bg-slate-800 rounded p-1">
                          <div className="text-[10px] text-muted-foreground">Pedido</div>
                          <div className="font-bold tabular-nums">{it.cantidad_pedida}</div>
                        </div>
                        <div className="bg-blue-100 dark:bg-blue-900/40 rounded p-1">
                          <div className="text-[10px] text-blue-700">Ya recibido</div>
                          <div className="font-bold tabular-nums text-blue-700">{it.cantidad_recibida}</div>
                        </div>
                        <div className="bg-amber-100 dark:bg-amber-900/40 rounded p-1">
                          <div className="text-[10px] text-amber-700">Pendiente</div>
                          <div className="font-bold tabular-nums text-amber-700">{it.cantidad_pendiente}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-xs whitespace-nowrap">Recibir hoy:</Label>
                        <Input
                          type="number"
                          step="any"
                          value={cant}
                          onChange={(e) => updateCantidad(it.item, e.target.value)}
                          className={`h-10 text-lg font-bold text-center tabular-nums ${
                            excede ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/30' : parcial ? 'border-blue-400' : ''
                          }`}
                          data-testid={`input-cant-${it.item}`}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateCantidad(it.item, it.cantidad_pendiente)}
                          className="rounded-xl whitespace-nowrap"
                          data-testid={`btn-max-${it.item}`}
                        >
                          Máx
                        </Button>
                      </div>
                      {excede && (
                        <div className="text-[11px] text-amber-700 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> Recibes más de lo pendiente. Confirma que es correcto.
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Total */}
          <Card>
            <CardContent className="p-3 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total a recepcionar</span>
              <span className="text-xl font-bold tabular-nums text-emerald-700">${formatCLP(totalRecep())}</span>
            </CardContent>
          </Card>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep(2)} className="rounded-xl flex-1">
              ← Volver
            </Button>
            <Button onClick={() => setStep(4)} className="rounded-xl flex-1 bg-blue-600 hover:bg-blue-700" data-testid="btn-ir-factura">
              <Camera className="w-4 h-4 mr-1.5" /> Continuar a Factura
            </Button>
          </div>
        </>
      )}

      {/* ═══════════════ PASO 4: Subir factura ═══════════════ */}
      {step === 4 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Camera className="w-5 h-5" /> Adjunta la Factura en Papel
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Button
                variant="outline"
                onClick={() => cameraInputRef.current?.click()}
                className="h-24 rounded-xl flex-col gap-1 border-2 border-dashed hover:border-blue-500 hover:bg-blue-50"
                data-testid="btn-camara"
              >
                <Camera className="w-6 h-6" />
                <span className="text-xs">Foto con cámara</span>
              </Button>
              <Button
                variant="outline"
                onClick={scannerShown ? usarUltimoEscaneo : conectarEscaner}
                disabled={!fsaSupported || scannerLoading}
                className="h-24 rounded-xl flex-col gap-1 border-2 border-dashed hover:border-purple-500 hover:bg-purple-50 disabled:opacity-50"
                title={fsaSupported ? (scannerShown ? 'Tomar el archivo más reciente de la carpeta' : 'Conectar con carpeta del escáner') : 'Solo disponible en Chrome/Edge'}
                data-testid="btn-escaner"
              >
                {scannerLoading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Printer className="w-6 h-6" />}
                <span className="text-xs">
                  {scannerShown ? 'Usar último escaneo' : 'Conectar Escáner'}
                </span>
                {!fsaSupported && <span className="text-[9px] text-amber-600">(Chrome/Edge)</span>}
              </Button>
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="h-24 rounded-xl flex-col gap-1 border-2 border-dashed hover:border-blue-500 hover:bg-blue-50"
                data-testid="btn-archivo"
              >
                <Upload className="w-6 h-6" />
                <span className="text-xs">Subir PDF/imagen</span>
              </Button>
            </div>
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileChange}
              className="hidden"
              data-testid="input-file-camera"
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              onChange={handleFileChange}
              className="hidden"
              data-testid="input-file-generic"
            />

            {/* Banner nuevo escaneo detectado (auto-polling) */}
            {nuevoEscaneo && !facturaFile && (
              <div className="border-2 border-emerald-500 rounded-lg p-3 bg-emerald-50 dark:bg-emerald-950/30 animate-pulse" data-testid="banner-nuevo-escaneo">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-emerald-800 font-semibold">Nuevo escaneo detectado</div>
                    <div className="font-mono text-sm truncate">{nuevoEscaneo.name}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {new Date(nuevoEscaneo.modified).toLocaleTimeString('es-CL')} · {(nuevoEscaneo.size / 1024).toFixed(0)}KB
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => seleccionarDelEscaner(nuevoEscaneo)}
                    className="bg-emerald-600 hover:bg-emerald-700 rounded-xl"
                    data-testid="btn-usar-nuevo-escaneo"
                  >
                    Usar
                  </Button>
                  <Button
                    size="icon" variant="ghost"
                    onClick={() => setNuevoEscaneo(null)}
                    className="rounded-xl h-8 w-8"
                  >
                    <XCircle className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Lista de archivos del escáner */}
            {scannerShown && (
              <div className="border-2 border-purple-200 rounded-lg p-3 bg-purple-50/60 dark:bg-purple-950/20 space-y-2" data-testid="scanner-file-list">
                <div className="flex items-center gap-2 text-xs font-semibold text-purple-800">
                  <Printer className="w-3.5 h-3.5" />
                  Archivos en la carpeta del escáner ({scannerFiles.length})
                  <Button
                    size="sm" variant="ghost" onClick={listarArchivosEscaner}
                    disabled={scannerLoading}
                    className="h-6 text-[11px] ml-auto"
                    data-testid="btn-refresh-scanner"
                  >
                    {scannerLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                    {' '}Actualizar
                  </Button>
                </div>
                {scannerFiles.length === 0 && !scannerLoading && (
                  <div className="text-xs text-muted-foreground text-center py-2">
                    Sin PDFs/imágenes recientes. Escanea la factura y haz click en Actualizar.
                  </div>
                )}
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {scannerFiles.map((sf) => (
                    <button
                      key={sf.name}
                      onClick={() => seleccionarDelEscaner(sf)}
                      className={`w-full text-left flex items-center gap-2 text-xs px-2 py-1.5 rounded border hover:border-purple-500 hover:bg-purple-100 dark:hover:bg-purple-900/30 ${
                        facturaFile?.name === sf.name ? 'border-purple-500 bg-purple-100 dark:bg-purple-900/40' : 'border-transparent bg-white/60 dark:bg-slate-900/40'
                      }`}
                      data-testid={`scanner-file-${sf.name}`}
                    >
                      <FileText className="w-3.5 h-3.5 flex-shrink-0 text-purple-600" />
                      <span className="font-mono text-[11px] flex-1 truncate">{sf.name}</span>
                      <span className="text-[10px] text-muted-foreground">
                        {new Date(sf.modified).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <span className="text-[10px] text-muted-foreground">{(sf.size / 1024).toFixed(0)}KB</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {facturaFile && (
              <div className="border rounded-lg p-3 bg-slate-50 dark:bg-slate-900/50 space-y-2">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm font-semibold flex-1 truncate">{facturaFile.name}</span>
                  <Button size="sm" variant="ghost" onClick={() => { setFacturaFile(null); setFacturaPreview(null); }}>
                    <XCircle className="w-4 h-4" />
                  </Button>
                </div>
                <div className="text-xs text-muted-foreground">
                  {(facturaFile.size / 1024).toFixed(1)} KB · {facturaFile.type || 'archivo'}
                </div>
                {facturaPreview && (
                  <img src={facturaPreview} alt="Preview" className="max-h-48 rounded border object-contain mx-auto" />
                )}
              </div>
            )}

            {!facturaFile && (
              <div className="text-xs text-center text-muted-foreground space-y-1">
                <div>Tip: la foto/PDF se guardará en SharePoint con la recepción para consulta posterior.</div>
                {fsaSupported && !scannerShown && (
                  <div className="text-[11px] text-purple-700">
                    💡 Primera vez: click en <strong>Conectar Escáner</strong> → selecciona la carpeta donde tu escáner guarda los PDFs (ej. <code>Documentos\Escaner</code>). Después basta con escanear y la app detecta el archivo solo.
                  </div>
                )}
                {fsaSupported && scannerShown && !nuevoEscaneo && (
                  <div className="text-[11px] text-emerald-700 flex items-center justify-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Monitoreando carpeta del escáner… escanea en tu impresora y aparecerá aquí automáticamente.
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setStep(3)} className="rounded-xl flex-1">
                ← Volver
              </Button>
              <Button
                onClick={guardarRecepcion}
                disabled={saving}
                className="rounded-xl flex-1 h-12 bg-emerald-600 hover:bg-emerald-700 text-base"
                data-testid="btn-confirmar-recepcion"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-5 h-5 mr-1.5" />}
                Confirmar Recepción
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
